/**
 * The shared copy-to-clipboard door's machinery (B306), a leaf module: no
 * JSX, no imports — ConfirmationCode (display.tsx) and ReferenceTag
 * (components.tsx, B551) both wear it.
 */

/**
 * B306 — what a press on the copy chip actually achieved.
 *
 * Four outcomes, not two, because their CAUSES differ and so do their
 * recoveries: the door has not been tried yet (claims nothing); the
 * clipboard took the text; the browser refused the write (the ordinary
 * one — the document is not focused); and the browser offered the page
 * no clipboard at all (an insecure context, `http://`, which is exactly
 * how a staging site is previewed). B305 learned the last two apart in
 * the admin's snippet door; this is the same vocabulary one layer down,
 * in the package, where every surface inherits it.
 */
export type CodeCopyOutcome = "untried" | "copied" | "refused" | "unavailable";

/** The minimum of `navigator.clipboard` this component needs, injectable. */
export interface ClipboardLike {
  readonly writeText: (text: string) => Promise<void>;
}

/**
 * The ONE mint of the outcome sentences (B294's `changeWords` shape,
 * walled in app-drift so no surface may hand-write a second copy).
 *
 * Both failures end in the SAME recovery, because the code is still on
 * screen and still selectable — which is why saying nothing was the whole
 * defect: the guest already had a way out and was not told they needed it.
 */
export function codeCopyWords(outcome: CodeCopyOutcome): string {
  switch (outcome) {
    case "untried":
      return "";
    case "copied":
      return "Code copied";
    case "refused":
      return "Your browser would not let this page reach the clipboard. Select the code above and copy it by hand.";
    case "unavailable":
      return "This browser gives the page no clipboard to write to. Select the code above and copy it by hand.";
  }
}

/**
 * The seam: the clipboard goes IN, the outcome comes OUT, so "a refused
 * write says so" is a test rather than a code review (B305's finding —
 * an outcome that lives only in component state is a fact no fleet net
 * can render).
 *
 * The rule it encodes: the settled "Copied" label is a claim about the
 * GUEST'S CLIPBOARD, so it may only be worn once the clipboard has taken
 * the text. Under-claiming is the honest direction — press again, or
 * select the code and copy it by hand — whereas over-claiming sends
 * someone to a counter to quote a code they do not have.
 */
export async function copyCodeEffect(args: {
  readonly code: string;
  readonly clipboard: ClipboardLike | undefined;
}): Promise<CodeCopyOutcome> {
  const clipboard = args.clipboard;
  // Not the same as a refusal: this browser gave the page no clipboard at
  // all (an insecure context), and the optional chain that used to stand
  // here made that indistinguishable from success.
  if (clipboard === undefined) return "unavailable";
  try {
    // AWAITED, not `void`-ed: a rejected write is the failure this item is
    // about, and `void promise` inside try/catch cannot see it (B305).
    await clipboard.writeText(args.code);
  } catch {
    return "refused";
  }
  return "copied";
}


/**
 * B564 — the same four outcomes, worded for a LINK a person hands to
 * someone else (`CopyLinkRow`). The recovery differs from a code's: the row
 * draws the address without its scheme, so selecting the face by hand would
 * copy a link that is not quite the link — the honest way back is OPEN,
 * whose address bar holds the whole thing.
 */
export function linkCopyWords(outcome: CodeCopyOutcome): string {
  switch (outcome) {
    case "untried":
      return "";
    case "copied":
      return "Link copied";
    case "refused":
      return "Your browser would not let this page reach the clipboard. Open it and copy the address from your browser instead.";
    case "unavailable":
      return "This browser gives the page no clipboard to write to. Open it and copy the address from your browser instead.";
  }
}
